/* eslint-disable sort-keys-plus/sort-keys */
//Disabled because of bug with the divider color inside a box component
import CircularLoading from '@/components/circular-loading';
import CollapsibleTable, { Order } from '@/components/collapsible-table';
import { PlanService } from '@/services/Plan.service';
import { userService } from '@/services/User.service';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  FormControlLabel,
  Switch,
  TablePagination,
  Theme,
  Typography
} from '@mui/material';
import { grey } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  initialPlansFilters,
  PlanFilters,
  planMainTypesOptions
} from '../plans/plans/consts';
import { PlansFilters } from '../plans/plans/PlansFilters/PlansFilters.component';
import { toSponsorPlanV1 } from '../plans/plans/PlansIndex/PlansIndex.component';
import PlanTableCell from '../plans/plans/PlansIndex/PlanTableCell.component';
import { checkPreviousStatusColumnOverdue } from '../plans/plans/PlansIndex/TrackerStatusCell.component';

const useStyles = makeStyles((theme: Theme) => ({
  margin: {
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(3)
  },
  planIdCellWidth: {
    width: theme.spacing(30)
  },
  planNameCellWidth: {
    width: theme.spacing(45)
  },
  textField: {
    width: '50ch'
  },
  tpaMaxWidth: {
    maxWidth: theme.spacing(20)
  }
}));

export const OnboardingStatusPage = () => {
  const classes = useStyles();

  const [showDelayedOnly, setShowDelayedOnly] = useState(false);
  const [sorter, setSorter] = useState<{ direction: Order; by: string }>({
    by: 'planName',
    direction: 'asc'
  });
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [planFilters, setPlanFilters] =
    useState<PlanFilters>(initialPlansFilters);
  const [isVestwellUser, setIsVestwellUser] = useState(false);
  const isTPA = userService.isTpaUser();

  const planListQuery = useQuery(
    [
      'PlanService.getPlansPage',
      {
        adminStatuses: ['Onboarding', 'Active'],
        filterType: planFilters.filterType,
        pageNumber: isTPA ? 0 : pageNumber,
        pageSize: isTPA ? 500 : rowsPerPage,
        planTypes: planFilters.types.length
          ? planFilters.types
          : planMainTypesOptions,
        recordkeepersIds: [5],
        search: planFilters.search,
        sortBy: sorter.by,
        sortDirection: sorter.direction,
        filterByActiveOnboardingEffectiveDate: true
      }
    ],
    async () => {
      const dto = await PlanService.getPlansPage({
        adminStatuses: ['Onboarding', 'Active'],
        filterType: planFilters.filterType,
        pageNumber: isTPA ? 0 : pageNumber,
        pageSize: isTPA ? 500 : rowsPerPage,
        planTypes: planFilters.types.length
          ? planFilters.types
          : planMainTypesOptions,
        recordkeepersIds: [5],
        search: planFilters.search,
        sortBy: sorter.by,
        sortDirection: sorter.direction,
        filterByActiveOnboardingEffectiveDate: true
      });

      return dto;
    },
    {
      staleTime: Infinity
    }
  );

  const planPizzaTrackers = useQuery(
    [
      'PlanService.getSponsorPlanPizzaTrackers',
      planListQuery.data?.data.map(plan => plan.sponsorPlanId)
    ],
    async () => {
      return await PlanService.getSponsorPlanPizzaTrackers(
        planListQuery.data?.data.map(plan => plan.sponsorPlanId)
      );
    },
    {
      enabled: !!planListQuery.data?.data.length,
      staleTime: Infinity
    }
  );

  const pizzaTrackerColumns: GridColDef[] = [
    {
      cellClassName: !isVestwellUser ? classes.planIdCellWidth : '',
      field: 'sponsorPlanId',
      headerName: 'Plan ID',
      width: 130
    },
    {
      cellClassName: !isVestwellUser ? classes.planNameCellWidth : '',
      field: 'planName',
      headerName: 'Plan Name',
      width: 130
    },
    {
      cellClassName: isTPA ? classes.tpaMaxWidth : '',
      field: 'status',
      headerName: 'Status'
    },
    {
      field: 'QUESTIONNAIRE_SUBMITTED_AT',
      headerName: 'Employer Questionnaire',
      sortable: false,
      width: 200
    },
    {
      field: 'PSA_SIGNED_DATE',
      headerName: 'Plan Services Agreement',
      sortable: false,
      width: 200
    },
    {
      field: 'PAYROLL_SETUP',
      headerName: 'Payroll & Employee Setup',
      sortable: false,
      width: 200
    },
    {
      field: 'DIGITAL_ONBOARDING_STATE_PLAN_LAUNCH_CALL',
      headerName: 'Plan Launch Call',
      sortable: false,
      width: 200
    },
    {
      field: 'CLIENT_SIGNED_PAA',
      headerName: 'Plan Adoption Agreement',
      sortable: false,
      width: 200
    },
    {
      field: 'EMPLOYEE_INVITES',
      headerName: 'Employee Invites',
      sortable: false,
      width: 200
    }
  ];

  const conversionColumns: GridColDef[] = [
    {
      field: 'BLACKOUT_START_DATE',
      headerName: 'Blackout Start Date',
      sortable: false,
      width: 200
    },
    {
      field: 'BLACKOUT_END_DATE',
      headerName: 'Blackout End Date',
      sortable: false,
      width: 200
    },
    {
      field: 'CONVERSION_WIRE_RECEIVED',
      headerName: 'Conversion Wire Received',
      sortable: false,
      width: 200
    }
  ];

  const plans = useMemo(() => {
    const pizzaMap = new Map(
      (planPizzaTrackers.data ?? []).map(p => [+p.VW_PLAN_ID, p])
    );

    return (planListQuery.data?.data || []).map(plan => {
      const v1 = toSponsorPlanV1(plan);
      const pizza = pizzaMap.get(v1.sponsorPlanId);

      const row = {
        ...v1,
        pizzaTracker: pizza
      };

      const withOverdue = pizzaTrackerColumns.some(column =>
        checkPreviousStatusColumnOverdue(row, column.field)
      );

      return {
        ...row,
        stats: {},
        withOverdue
      };
    });
  }, [isTPA, planListQuery.data, planPizzaTrackers.data]);

  const filteredPlans = useMemo(() => {
    return showDelayedOnly ? plans.filter(p => p.withOverdue) : plans;
  }, [plans, showDelayedOnly]);

  const paginatedPlans = useMemo(() => {
    const start = pageNumber * rowsPerPage;
    const end = start + rowsPerPage;
    return isTPA ? filteredPlans.slice(start, end) : filteredPlans;
  }, [filteredPlans, pageNumber, rowsPerPage]);

  const newPlans = useMemo(
    () => paginatedPlans.filter(p => !p.isConvert),
    [paginatedPlans]
  );
  const conversionPlans = useMemo(
    () => paginatedPlans.filter(p => p.isConvert),
    [paginatedPlans]
  );

  const renderTable = (title: string, data: any[]) => (
    <Box bgcolor='white' border={1} borderColor='divider' mb={4}>
      <Typography fontWeight={600} m={2} mb={1} ml={2} variant='h6'>
        {title}
      </Typography>
      <CollapsibleTable
        backgroundPaperElevation={0}
        cellComponent={PlanTableCell}
        columns={[
          ...pizzaTrackerColumns,
          ...(title === 'Conversion Plans' ? conversionColumns : []),

          {
            field: 'FIRST_PAYROLL',
            headerName: 'First Payroll',
            sortable: false,
            width: 200
          }
        ]}
        disablePagination={true}
        isLoading={planListQuery.isFetching || planPizzaTrackers.isFetching}
        noDataPlaceholderComponent={
          <Box
            alignItems='center'
            display='flex'
            flexDirection='column'
            gap={1}
            py={6}>
            {planListQuery.isFetching ? (
              <CircularLoading />
            ) : (
              <>
                <SearchIcon sx={{ color: 'common.black', fontSize: 40 }} />
                <Typography variant='body2'>No plans found</Typography>
              </>
            )}
          </Box>
        }
        rootPaperElevation={0}
        sorter={{
          onSortOrderChanged: (newOrderBy, newOrder) => {
            setSorter({ by: newOrderBy, direction: newOrder });
          },
          order: sorter.direction,
          orderBy: sorter.by
        }}
        tableData={data}
        useSquareBottomContainer
      />
    </Box>
  );

  const updateFilters = useCallback((newFilters: PlanFilters) => {
    setPlanFilters(newFilters);
    setPageNumber(0);
  }, []);

  useEffect(() => {
    const subscriptionId = 'OnboardingStatusPage';
    userService.subscribe(subscriptionId, ({ token }) => {
      setIsVestwellUser(!!token && !token.tpaId);
    });
    return () => userService.unsubscribe(subscriptionId);
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(0);
  };

  return (
    <Box display='flex' flexDirection='column'>
      <Box display='flex'>
        <PlansFilters
          areFiltersApplied={false}
          extraFilters={
            isTPA && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showDelayedOnly}
                    onChange={() => setShowDelayedOnly(prev => !prev)}
                  />
                }
                label='Plans with Delays'
              />
            )
          }
          hideMeta
          hideProgram
          isModal
          isTPA={isTPA}
          isVestwellUser={isVestwellUser}
          planTypes={planMainTypesOptions}
          updateFilters={updateFilters}
        />

        <Box
          bgcolor={grey[50]}
          flex={1}
          overflow='auto'
          p={3}
          sx={{
            borderTop: 1,
            borderRight: 1,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
          {renderTable('New Plans', newPlans)}
          {renderTable('Conversion Plans', conversionPlans)}
        </Box>
      </Box>
      <TablePagination
        component='div'
        count={
          isTPA ? filteredPlans.length : planListQuery.data?.meta.count || 0
        }
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        page={pageNumber}
        rowsPerPage={rowsPerPage}
      />
    </Box>
  );
};
